<template>

	<!-- Header -->
	<Head :title="formData.title"/>

	<main>
		<div class="container col-5 mobile-fix-container">
			<div id="landing-page-form" class="mt-4 mb-4 mobile-fix-rounded">
				<div class="row justify-content-center mobile-fix-margin">
					<div class="col mobile-fix-padding">

						<!-- Form -->
						<form v-show="! form.processing" ref="payment_form" method="POST" class="p-1 payment-form">

							<!-- Step 1 -->
							<h4 class="mb-3">
								<div class="badge badge-danger step">
									{{ $t('donation-form.step') }} 1
								</div>
								{{ $t('donation-form.summary_section_title') }}
							</h4>

							<!-- Hide from view -->
							<div style="display:none;">

								<!-- Donor Details -->
								<div>
                  <donor-details
                      v-model:participant_name="form.participant_name"
                      v-model:participant_tel_no="form.participant_tel_no"
                      v-model:participant_email="form.participant_email"
                      :recurring_transaction_enabled="props.formData.recurring_transaction_enabled"
                      :participant_email_required="props.formData.participant_email_required"
                      :mandate_enabled="props.formData.mandate_enabled"
                      :mandate_application_type="props.formData.mandate_application_type"
                      :errors="form.errors"
                  />

									<!-- Address -->
                  <donor-address
                      v-model:participant_address1="form.participant_address1"
                      v-model:participant_address2="form.participant_address2"
                      v-model:participant_postcode="form.participant_postcode"
                      v-model:participant_city="form.participant_city"
                      v-model:participant_state="form.participant_state"
                      v-model:participant_country="form.participant_country"
                      :participant_address_field_enabled="props.formData.participant_address_field_enabled"
                      :participant_email_required="props.formData.participant_email_required"
                      :participant_address_field_required="props.formData.participant_address_field_required"
                      :mandate_enabled="props.formData.mandate_enabled"
                      :mandate_application_type="props.formData.mandate_application_type"
                      :city_opts="props.cityOpts"
                      :state_opts="props.stateOpts"
                      :country_opts="props.countryOpts"
                      :default_country="props.formData.default_country"
                      :errors="form.errors"
                  />
								</div>

								<!-- Tax Exemption -->
                <tax-details
                    v-model:exclude_tax="form.exclude_tax"
                    v-model:tax_ic_no="form.tax_ic_no"
                    v-model:tax_address1="form.tax_address1"
                    v-model:tax_address2="form.tax_address2"
                    v-model:tax_postcode="form.tax_postcode"
                    v-model:tax_city="form.tax_city"
                    v-model:tax_state="form.tax_state"
                    v-model:tax_country="form.tax_country"
                    :tenant_can_issue_tax_receipt="props.formData.tenant_can_issue_tax_receipt"
                    :city_opts="props.cityOpts"
                    :state_opts="props.stateOpts"
                    :country_opts="props.countryOpts"
                    :default_country="props.formData.default_country"
                    :errors="form.errors"
                />
							</div>

							<!-- Table donation details -->
							<table class="table table-bordered mb-4">
								<thead>
									<tr>
										<th class="align-middle th-details">
											{{ $t('donation-form.summary_details_col_header') }}
										</th>
										<th class="align-middle text-right th-quantity">
											{{ $t('donation-form.summary_quantity_col_header') }}
										</th>
										<th class="align-middle text-right th-amount">
											{{ $t('donation-form.summary_amount_col_header') }}
										</th>
									</tr>
								</thead>
								<tbody>
									<template v-for="(donations, campaign) in transactionData.summary" :key="campaign">
										<template v-if="donations instanceof Object" v-for="donation in donations" :key="donation">

											<!-- Campaign -->
											<tr class="bg-light">
												<th colspan="3">
													{{ campaign }}
												</th>
											</tr>
											<tr>

												<!-- Package name -->
												<td role="gridcell" class="align-middle border-b border-gray-200">
													{{ donation.name }}
												</td>

												<!-- Package count unit quantity -->
												<td role="gridcell" class="align-middle border-b border-gray-200 text-right">
													x {{ donation.count }}
												</td>

												<!-- Amount -->
												<td role="gridcell" class="align-middle border-b border-gray-200 text-right">
													{{ vueNumberFormat(donation.amount * donation.count) }}
												</td>
											</tr>
										</template>
									</template>

									<!-- Total -->
									<tr>
										<th colspan="2" class="align-middle text-right">
											{{ $t('donation-form.total') }}
										</th>
										<th class="align-middle text-right">
											{{ vueNumberFormat(transactionData.order_amount) }}
										</th>
									</tr>

									<!-- Payment gateway fees -->
									<tr v-if="formData.payment_gateway_fees_enabled">
										<th colspan="3" class="align-middle text-right">
											<span class="text-normal alert-warning p-1">
												{{ $t('donation-form.payment_gateway_fees').replace('#amount#', paymentGatewayFee) }}
											</span>
										</th>
									</tr>
								</tbody>
							</table>

							<!-- Payment Methods -->
							<div class="border-top pt-4 mt-3">
                <payment-methods
                    v-model:payment_gateway.number="form.payment_gateway"
                    v-model:merchant_bank_account="form.merchant_bank_account"
                    v-model:bank_transfer_type="form.bank_transfer_type"
                    v-model:bank_transfer_date="form.bank_transfer_date"
                    v-model:bank_transfer_notes="form.bank_transfer_notes"
                    v-model:proof_of_payment="form.proof_of_payment"
                    v-model:fpx_buyer_bank="form.fpx_buyer_bank"
                    v-model:fpx_buyer_bank_code="form.fpx_buyer_bank_code"
                    v-model:fpx_buyer_bank_name="form.fpx_buyer_bank_name"
                    :mandate_enabled="formData.mandate_enabled"
                    :paymentGateways="paymentGateways"
                    :fpx_gateway_code="formData.fpx_gateway_code"
                    :tenant="tenant"
                    :form_code="props.formData.code"
                    :manual_bank_transfer_enabled="formData.manual_bank_transfer_enabled"
                    :manual_bank_transfer_gateway_code="formData.manual_bank_transfer_gateway_code"
                    :manual_bank_transfer_payment_notes="formData.manual_bank_transfer_payment_notes"
                    :bankAccounts="bankAccounts"
                    :fpxBanksList="fpxBanksList"
                    :fpxBanksLogo="fpxBanksLogo"
                    :total="form.order_amount"
                    :processing="form.processing"
                    :errors="form.errors"
                />
							</div>

							<!-- Button -->
							<div class="text-center align-items-baseline justify-content-center mb-3">
								<button type="button" class="btn btn-lg btn-success btn-block-modified" @click="submit">
									{{ $t('donation-form.proceed_to_donation') }}
									<i class="fad fa-angle-double-right ml-1"></i>
								</button>
							</div>
						</form>

						<!-- Redirecting to Payment Gateway -->
						<div v-show="form.processing">
							<div class="my-5 d-flex flex-column align-items-center justify-content-center">
								<h5 class="text-center">
									<template v-if="form.payment_gateway == formData.manual_bank_transfer_gateway_code">
										{{ $t('donation-form.processing_manual_bank_transfer') }}
									</template>

									<template v-else>
										{{ $t('donation-form.redirect_to_payment_gateway') }}
									</template>
								</h5>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Footer -->
			<footer-section :tenant="tenant"/>
		</div>
	</main>
</template>

<!-- Scripts -->
<script setup>
import DonorDetails from "@/Pages/Donations/DonorDetails.vue";
import DonorAddress from "@/Pages/Donations/DonorAddress.vue";
import TaxDetails from "@/Pages/Donations/TaxDetails.vue";
import PaymentMethods from "@/Pages/Donations/PaymentMethods.vue";

import FooterSection from "@/Components/FooterSection.vue";
import {computed, onMounted, ref, watch} from "vue";
import {Head, useForm} from "@inertiajs/vue3";
import useTrackingCode from "@/Composables/UseTrackingCode";
import useHelpers from "@/Composables/UseHelpers";
import InitializeFormCustomFieldsValue from "@/Lib/InitializeFormCustomFieldsValue";

const {$t, capitalize, calculatePaymentGatewayFee} = useHelpers();

const {getFbClientId, sendFacebookPixelInitiateCheckoutEvent, sendGoogleAdsBeginCheckoutEvent} = useTrackingCode();

const emit = defineEmits(['processing', 'done-processing']);

const props = defineProps({
	formData: Object,
	transactionData: Object,
	participantAddressFieldEnabled: Boolean | Number,
	summary: Object,
	cityOpts: Object,
	stateOpts: Object,
	countryOpts: Object,
	bankAccounts: Object,
	paymentGateways: Object,
  fpxBanksList: Object,
  fpxBanksLogo: Object,
	tenant: Object,
});

Lang.setLocale(props.formData.language ?? 'en')

const payment_form = ref(null);

const form = useForm({
	form_id: props.transactionData.form_id,
	form_language: props.formData.language,
	participant_name: props.transactionData.participant_name,
	participant_email: props.transactionData.participant_email,
	participant_tel_no: props.transactionData.participant_tel_no,
	order_amount: props.transactionData.order_amount,

	is_recurring: 1,

	// Marketing consent
	participant_sms_email_marketing_consent: false,

	// Address (if enable)
	participant_address1: props.transactionData.participant_address1,
	participant_address2: props.transactionData.participant_address2,
	participant_postcode: props.transactionData.participant_postcode,
	participant_city: props.transactionData.participant_city,
	participant_state: props.transactionData.participant_state,
	participant_country: props.transactionData.participant_country,

	// Tax exemption
	exclude_tax: false,
	tax_ic_no: props.transactionData.tax_ic_no,
	tax_address1: props.transactionData.tax_address1,
	tax_address2: props.transactionData.tax_address2,
	tax_postcode: props.transactionData.tax_postcode,
	tax_city: props.transactionData.tax_city,
	tax_state: props.transactionData.tax_state,
	tax_country: props.transactionData.tax_country,

  // Custom data field 1
  custom_field_1_data_custom_enabled: '',
  custom_field_1_required: '',
  custom_field_1_label: '',
  custom_field_1_data: '',
  custom_field_1_last_data: '',
  custom_field_1_value: '',
  custom_field_1_custom: '',

  // Custom data field 2
  custom_field_2_data_custom_enabled: '',
  custom_field_2_required: '',
  custom_field_2_label: '',
  custom_field_2_data: '',
  custom_field_2_last_data: '',
  custom_field_2_value: '',
  custom_field_2_custom: '',

  // Custom data field 3
  custom_field_3_data_custom_enabled: '',
  custom_field_3_required: '',
  custom_field_3_label: '',
  custom_field_3_data: '',
  custom_field_3_last_data: '',
  custom_field_3_value: '',
  custom_field_3_custom: '',

	// Payment method
	payment_gateway: 1,
	merchant_bank_account: props.bankAccounts[0],
	bank_transfer_type: 'Internet Banking',
	bank_transfer_date: new Date().toISOString().slice(0, 10),
	bank_transfer_notes: '',
	proof_of_payment: '',

	from_recurring_transaction: true,

	summary: props.transactionData.summary,
});

watch(() => props.total, (val) => {
	form.order_amount = val;
});

const paymentGatewayFee = computed(() => {
	const gateway = props.paymentGateways.find((g) => g.code === form.payment_gateway);

	return gateway ? calculatePaymentGatewayFee(gateway, form.order_amount).display : 'RM0.00';
});

onMounted(() => {
  InitializeFormCustomFieldsValue(props, form);

  // Set _fbp && _fbc cookie value
  form.summary._fbp = getFbClientId();
});

function submit() {
	let url = window.location.href;

	form.clearErrors();

	emit('processing');

	if (form.payment_gateway !== props.formData.manual_bank_transfer_gateway_code) {
		form.bank_transfer_type = null;
		form.bank_transfer_date = null;
		form.merchant_bank_account = null;
		form.bank_transfer_type = null;
	}

	form.post(url, {
		preserveScroll: true,
		preserveState: true,

    onSuccess: new Promise(function (resolve) {
      return resolve();
    }),
	})
}
</script>